import React, { useEffect, useMemo, useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend
} from 'recharts';
import { calculateRouteCost } from '../../logistics/components/LogisticsUtils';
import {
  getOfficeLocationFromSettings,
  getTimestampMillis,
  calculateDistance,
  formatCurrency
} from '../utils/routeMonitorUtils';
import { getTransportVisualConfig } from '../../logistics/components/mapUtils';
import { resolveTransportIdentity } from '../../logistics/components/transportIdentity';

const Icon = ({ path, className = "w-6 h-6" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);

export const DetailedTransporteView = ({ title, data, color, period }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'reportDateTime', direction: 'desc' });

  const resolveDateMillis = (item) => {
    const value = item?.reportDateTime || item?.closedAt || item?.finishedAt || item?.completedAt || item?.updatedAt || (item?.date ? `${item.date}T00:00:00` : null);
    if (!value) return 0;
    if (typeof value?.toDate === 'function') {
      try {
        return value.toDate().getTime();
      } catch {
        return 0;
      }
    }
    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
  };

  const chartSourceData = useMemo(() => {
    return [...data].sort((a, b) => resolveDateMillis(a) - resolveDateMillis(b));
  }, [data]);

  const tableData = useMemo(() => {
    const rows = [...data];
    rows.sort((a, b) => {
      let comparison = 0;

      if (sortConfig.key === 'reportDateTime') {
        comparison = resolveDateMillis(a) - resolveDateMillis(b);
      } else if (sortConfig.key === 'name') {
        comparison = String(a?.name || '').localeCompare(String(b?.name || ''), 'es');
      } else if (sortConfig.key === 'value') {
        comparison = Number(a?.value || 0) - Number(b?.value || 0);
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return rows;
  }, [data, sortConfig]);

  const requestSort = (key) => {
    setSortConfig((current) => (
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'reportDateTime' ? 'desc' : 'asc' }
    ));
  };

  const chartData = useMemo(() => {
    if (period === 'year') {
      const monthlyTotals = chartSourceData.reduce((acc, item) => {
        const monthKey = item.date.substring(0, 7);
        acc[monthKey] = (acc[monthKey] || 0) + item.value;
        return acc;
      }, {});

      let cumulativeTotal = 0;
      return Object.keys(monthlyTotals)
        .sort()
        .map((monthKey) => {
          cumulativeTotal += monthlyTotals[monthKey];
          return {
            date: monthKey,
            value: cumulativeTotal
          };
        });
    }

    return chartSourceData;
  }, [chartSourceData, period]);

  const formatDateTick = (tick) => {
    if (period === 'year') {
      const date = new Date(tick + '-02T12:00:00Z');
      return date.toLocaleDateString('es-AR', { month: 'short', year: '2-digit' });
    }

    const date = new Date(tick + 'T00:00:00');
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const dayName = date
      .toLocaleDateString('es-AR', { weekday: 'short' })
      .replace('.', '');

    return `${dayName.charAt(0).toUpperCase() + dayName.slice(1)} ${day}/${month}`;
  };

  return (
    <div className="p-4 rounded-lg bg-white/50 backdrop-blur-sm shadow-lg grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        <h4 className="text-xl font-bold text-slate-800 mb-4">
          {title} {period === 'year' && '(Acumulado)'}
        </h4>

        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 50, bottom: 0 }}>
            <defs>
              <linearGradient id={`colorDetail-${color}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.8} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" tickFormatter={formatDateTick} />
            <YAxis tickFormatter={(value) => `$${formatCurrency(value)}`} />
            <Tooltip formatter={(value) => `$ ${formatCurrency(value)}`} />
            <Area
              type="monotone"
              dataKey="value"
              name={period === 'year' ? 'Acumulado' : 'Costo'}
              stroke={color}
              strokeWidth={2}
              fillOpacity={1}
              fill={`url(#colorDetail-${color})`}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="max-h-[360px] overflow-y-auto">
        <h4 className="text-xl font-bold text-slate-800 mb-4">Datos</h4>

        <table className="min-w-full text-sm">
          <thead className="bg-slate-200 sticky top-0 z-10">
            <tr>
              <th className="p-2 text-left">
                <button type="button" onClick={() => requestSort('reportDateTime')} className="inline-flex items-center gap-1 font-semibold">
                  Fecha
                </button>
              </th>
              <th className="p-2 text-left">
                <button type="button" onClick={() => requestSort('name')} className="inline-flex items-center gap-1 font-semibold">
                  Ruta
                </button>
              </th>
              <th className="p-2 text-right">
                <button type="button" onClick={() => requestSort('value')} className="inline-flex items-center gap-1 font-semibold">
                  Costo
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {tableData.map((item) => (
              <tr key={item.id} className="hover:bg-slate-100">
                <td className="p-2">
                  {(() => {
                    const raw = item?.reportDateTime || item?.date;
                    const parsed = raw instanceof Date ? raw : new Date(raw && String(raw).includes('T') ? raw : `${raw}T00:00:00`);
                    return Number.isNaN(parsed.getTime())
                      ? 'Sin fecha'
                      : parsed.toLocaleString('es-AR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        });
                  })()}
                </td>
                <td className="p-2">{item.name}</td>
                <td className="p-2 text-right font-semibold">$ {formatCurrency(item.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// --- Detalle Flota ---
export const DetailedFlotaView = ({ title, data, colors }) => {
  const pieData = useMemo(() => {
    const grouped = data.reduce((acc, item) => {
      acc[item.name] = (acc[item.name] || 0) + item.value;
      return acc;
    }, {});
    return Object.entries(grouped).map(([name, value]) => ({ name, value }));
  }, [data]);

  return (
    <div className="p-4 rounded-lg bg-white/50 backdrop-blur-sm shadow-lg grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        <h4 className="text-xl font-bold text-slate-800 mb-4">{title}</h4>

        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={100}
              labelLine={false}
              label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                const RADIAN = Math.PI / 180;
                const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                const y = cy + radius * Math.sin(-midAngle * RADIAN);

                return (
                  <text
                    x={x}
                    y={y}
                    fill="white"
                    textAnchor={x > cx ? 'start' : 'end'}
                    dominantBaseline="central"
                    className="font-bold"
                  >
                    {`${(percent * 100).toFixed(0)}%`}
                  </text>
                );
              }}
            >
              {pieData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={colors[entry.name] || colors['Otros']}
                />
              ))}
            </Pie>
            <Tooltip formatter={(value) => `$ ${formatCurrency(value)}`} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="max-h-[360px] overflow-y-auto">
        <h4 className="text-xl font-bold text-slate-800 mb-4">Desglose de Gastos</h4>

        <table className="min-w-full text-sm">
          <thead className="bg-slate-200 sticky top-0 z-10">
            <tr>
              <th className="p-2 text-left">Fecha</th>
              <th className="p-2 text-left">Categoría</th>
              <th className="p-2 text-left">Unidad</th>
              <th className="p-2 text-left">Chofer</th>
              <th className="p-2 text-left">Descripción</th>
              <th className="p-2 text-right">Monto</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.map((item) => {
              const transportIdentity = resolveTransportIdentity(item);
              const transportVisual = getTransportVisualConfig(item.transportType || 'camioneta');
              return (
                <tr key={item.id} className="hover:bg-slate-100">
                  <td className="p-2">{new Date(item.date + 'T00:00:00').toLocaleDateString('es-AR')}</td>
                  <td className="p-2">{item.name}</td>
                  <td className="p-2">
                    <div className="flex items-center gap-2">
                      <div
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border"
                        style={{
                          backgroundColor: item.identitySoftColor || transportIdentity.identitySoftColor,
                          borderColor: item.identityColor || transportIdentity.identityColor,
                          color: item.identityColor || transportIdentity.identityColor,
                        }}
                      >
                        <Icon path={item.transportIconPath || transportVisual.path} className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-800">{item.transportName || 'Unidad sin identificar'}</div>
                        <div className="text-xs text-slate-500">{item.transportMeta || 'Sin referencia operativa'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-2 text-slate-600">
                    {item.driverName ? (
                      <div>
                        <div className="font-semibold text-slate-800">{item.driverName}</div>
                        <div className="text-xs text-slate-500">{item.driverMeta || item.categoryScopeLabel || 'Impacto registrado en chofer'}</div>
                      </div>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="p-2 text-slate-600">{item.description || '-'}</td>
                  <td className="p-2 text-right font-semibold">$ {formatCurrency(item.value)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// --- Detalle Kilómetros ---
export const DetailedKmsView = ({ title, data, settings }) => {
  const [selectedRoute, setSelectedRoute] = useState(null);

  useEffect(() => {
    if (selectedRoute && !data.find((item) => item.id === selectedRoute.id)) {
      setSelectedRoute(null);
    }
  }, [data, selectedRoute]);

  return (
    <div className="p-4 rounded-lg bg-white/50 backdrop-blur-sm shadow-lg grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="max-h-[400px] overflow-y-auto">
        <h4 className="text-xl font-bold text-slate-800 mb-4">{title}</h4>

        <table className="min-w-full text-sm">
          <thead className="bg-slate-200 sticky top-0 z-10">
            <tr>
              <th className="p-2 text-left">Fecha</th>
              <th className="p-2 text-left">Ruta</th>
              <th className="p-2 text-right">Distancia</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr
                key={item.id}
                className={`border-b cursor-pointer transition-colors ${
                  selectedRoute?.id === item.id ? 'bg-blue-100' : 'hover:bg-slate-100'
                }`}
                onClick={() => setSelectedRoute(item)}
              >
                <td className="p-2">{new Date(item.date + 'T00:00:00').toLocaleDateString('es-AR')}</td>
                <td className="p-2">{item.name}</td>
                <td className="p-2 text-right font-semibold">{item.value.toFixed(2)} km</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="relative min-h-[400px]">
        {selectedRoute ? (
          <RouteDetailPanel key={selectedRoute.id} route={selectedRoute} settings={settings} />
        ) : (
          <div className="flex items-center justify-center h-full text-slate-500 bg-slate-50 rounded-lg">
            <p className="text-center font-semibold">
              Seleccione una ruta de la lista
              <br />
              para ver su detalle.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// --- Panel detalle ruta ---
export const RouteDetailPanel = ({ route, settings }) => {
  const officeLocation = useMemo(
    () => getOfficeLocationFromSettings(settings),
    [settings]
  );

  const { totalCost, legCosts } = useMemo(
    () => calculateRouteCost({ route, settings, officeLocation }),
    [route, settings, officeLocation]
  );

  const reportEntries = useMemo(() => {
    if (!route || !route.destinations) return [];

    const completedOrIncomplete = route.destinations
      .filter(
        (d) =>
          (d.status === 'completed' || d.status === 'incomplete') &&
          d.completedAt &&
          d.latlng &&
          typeof d.latlng.lat === 'number' &&
          typeof d.latlng.lng === 'number'
      )
      .sort((a, b) => getTimestampMillis(a.completedAt) - getTimestampMillis(b.completedAt));

    const cancelled = route.destinations.filter((d) => d.status === 'cancelled');

    let lastPointCoords = { lat: officeLocation.lat, lng: officeLocation.lng };
    const entries = [];

    completedOrIncomplete.forEach((dest, index) => {
      const distance = calculateDistance(
        lastPointCoords.lat,
        lastPointCoords.lng,
        dest.latlng.lat,
        dest.latlng.lng
      );

      entries.push({
        stopNumber: index + 1,
        destinationName: dest.name,
        status: dest.status === 'completed' ? 'Completado' : 'Incompleto',
        time:
          typeof dest.completedAt?.toDate === 'function'
            ? dest.completedAt.toDate().toLocaleTimeString('es-AR', {
                hour: '2-digit',
                minute: '2-digit'
              })
            : new Date(dest.completedAt).toLocaleTimeString('es-AR', {
                hour: '2-digit',
                minute: '2-digit'
              }),
        distance: distance.toFixed(2),
        cost: legCosts[dest.id] || 0
      });

      lastPointCoords = dest.latlng;
    });

    cancelled.forEach((dest) =>
      entries.push({
        stopNumber: entries.length + 1,
        destinationName: dest.name,
        status: 'Cancelado',
        time: 'N/A',
        distance: '0.00',
        cost: 0
      })
    );

    return entries;
  }, [route, legCosts, officeLocation]);

  const totalDistance = useMemo(() => {
    return reportEntries.reduce((sum, entry) => sum + parseFloat(entry.distance || 0), 0).toFixed(2);
  }, [reportEntries]);

  const handlePrint = () => {
    const reportElement = document.getElementById(`printable-report-${route.id}`);
    if (!reportElement) return;

    const printContents = reportElement.innerHTML;
    const originalContents = document.body.innerHTML;
    document.body.innerHTML = printContents;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  const handleDownload = () => {
    const reportElement = document.getElementById(`printable-report-${route.id}`);
    if (reportElement) {
      html2canvas(reportElement).then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save(`informe-ruta-${route.date}.pdf`);
      });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md h-full flex flex-col animate-fade-in">
      <div id={`printable-report-${route.id}`} className="flex-grow flex flex-col">
        <div className="flex justify-between items-center p-3 border-b">
          <div>
            <h4 className="font-bold text-slate-800">{route.name}</h4>
            <p className="text-sm text-slate-500">
              {new Date(route.date + 'T00:00:00').toLocaleDateString('es-AR')}
            </p>
          </div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handlePrint}
              title="Imprimir"
              className="p-2 rounded-md hover:bg-slate-100 text-slate-600"
            >
              <Icon
                path="M6.72 13.829c-.34.052-.668.087-.99.111v-2.42a.75.75 0 01.75-.75h13.5a.75.75 0 01.75.75v2.42a49.048 49.048 0 01-.99-.111c-.34-.052-.668-.087-.99-.111m-11.454 0c1.297-.138 2.65-.25 4.005-.327m-4.005.327c1.297-.138 2.65-.25 4.005.327m7.449 0c1.355.077 2.708.189 4.005.327M4.5 9.75a.75.75 0 01.75-.75h13.5a.75.75 0 01.75.75v10.5a.75.75 0 01-.75-.75H5.25a.75.75 0 01-.75-.75V9.75z"
                className="w-5 h-5"
              />
            </button>

            <button
              type="button"
              onClick={handleDownload}
              title="Descargar PDF"
              className="p-2 rounded-md hover:bg-slate-100 text-slate-600"
            >
              <Icon
                path="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                className="w-5 h-5"
              />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-grow p-3 text-xs">
          <table className="min-w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="p-1 text-left font-semibold">Destino</th>
                <th className="p-1 text-left font-semibold">Estado</th>
                <th className="p-1 text-right font-semibold">Km</th>
                <th className="p-1 text-right font-semibold">Costo</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {reportEntries.map((entry) => (
                <tr key={entry.stopNumber}>
                  <td className="p-1">{entry.destinationName}</td>
                  <td className="p-1">{entry.status}</td>
                  <td className="p-1 text-right">{entry.distance}</td>
                  <td className="p-1 text-right font-medium">{formatCurrency(entry.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="p-3 border-t mt-auto">
          <div className="flex justify-between font-bold">
            <span>Total:</span>
            <div className="text-right">
              <p>{totalDistance} km</p>
              <p>$ {formatCurrency(totalCost)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
